import { Autocomplete, Popper, createFilterOptions } from '@mui/material';
import type React from 'react';
import { useEffect, useState } from 'react';
import { Field, useField, useForm } from 'react-final-form';

import withValidator from '../../hoc/withValidator';
import { usePauseValidator } from '../../hooks/usePauseValidator/usePauseValidator';
import { type Props as InputProps } from '../Input/Input';
import RawInput from '../RawInput/RawInput';

export interface Option {
  id?: string;
  text: string;
}

interface Props extends InputProps {
  options: Option[];
  matchFrom?: 'any' | 'start';
  limit?: number;
  dataTestId?: string;
  shouldFocus?: boolean;
}

interface MuiInputProps {
  ref: React.RefObject<HTMLTextAreaElement> & React.RefObject<HTMLInputElement>;
  value: string;
}

const StarsAutocomplete: React.FC<Props> = (props) => {
  const form = useForm();
  const [open, setOpen] = useState(false);
  usePauseValidator();
  const {
    id,
    name,
    options,
    matchFrom,
    limit,
    recordValidator,
    validator,
    dataTestId,
    shouldFocus
  } = props;

  const {
    input: { value }
  } = useField(id, { subscription: { value: true } });

  useEffect(() => {
    if (shouldFocus && value === '') {
      setOpen(true);
    }
  }, [shouldFocus, value]);

  const filterOptions = createFilterOptions({
    matchFrom,
    limit,
    stringify: (option: Option) => option.text
  });

  const handleOnFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    if (event.target.id === id) setOpen(true);
  };

  return (
    <Field name={id || name} component="input" validate={recordValidator || validator}>
      {({ input, meta }) => (
        <Autocomplete
          autoHighlight
          disablePortal
          data-testid={dataTestId}
          id={id}
          open={open}
          options={options}
          getOptionLabel={(option) => option.text}
          filterOptions={filterOptions}
          onInputChange={(_, value) => {
            form.change(id, value);
            setOpen(true);
          }}
          onFocus={handleOnFocus}
          onClose={() => setOpen(false)}
          PopperComponent={({ style, ...props }) => (
            <Popper {...props} style={{ ...style, zIndex: 1 }} />
          )}
          renderInput={(params) => {
            const { ref: inputRef, value: rawValue, ...rest } = params.inputProps as MuiInputProps;
            const inputProps = { inputRef, rawValue, ...rest };

            return (
              <div ref={params.InputProps.ref}>
                <RawInput {...inputProps} {...props} input={input} meta={meta} />
              </div>
            );
          }}
        />
      )}
    </Field>
  );
};

export default withValidator(StarsAutocomplete);
